import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";

export interface TemplateTranslations {
  [lang: string]: string;
}

export interface QuoteLineItemTemplate {
  id: string;
  name: TemplateTranslations;
  code: string | null;
  default_amount: number;
  default_unit_price: number;
  line_item_type: "generic" | "equipment" | "crew";
  category: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroQuoteLineItemTemplateRow = {
  id: string;
  name: TemplateTranslations;
  code?: string | null;
  defaultAmount: number;
  defaultUnitPrice: number;
  lineItemType: "generic" | "equipment" | "crew";
  category?: string | null;
  sortOrder: number;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyTemplate(
  row: ZeroQuoteLineItemTemplateRow,
): QuoteLineItemTemplate {
  return {
    id: row.id,
    name: row.name,
    code: row.code ?? null,
    default_amount: row.defaultAmount,
    default_unit_price: row.defaultUnitPrice,
    line_item_type: row.lineItemType,
    category: row.category ?? null,
    sort_order: row.sortOrder,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

function sortTemplates(templates: QuoteLineItemTemplate[]) {
  return [...templates].sort((a, b) => {
    const aName = resolveTemplateName(a.name, "de");
    const bName = resolveTemplateName(b.name, "de");
    const aSort = a.code ? `${a.code} ${aName}` : aName;
    const bSort = b.code ? `${b.code} ${bName}` : bName;
    return aSort.localeCompare(bSort);
  });
}

export function useQuoteLineItemTemplates() {
  const [data, details] = useZeroQuery(
    appQueries.quoteLineItemTemplates.list(),
  );
  const templates = sortTemplates(
    ((data ?? []) as ZeroQuoteLineItemTemplateRow[]).map(toLegacyTemplate),
  );

  return {
    data: templates,
    isLoading: details.type !== "complete" && templates.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateQuoteLineItemTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      name: TemplateTranslations;
      code?: string | null;
      default_amount?: number;
      default_unit_price?: number;
      line_item_type?: "generic" | "equipment" | "crew";
      category?: string | null;
      sort_order?: number;
    },
    QuoteLineItemTemplate
  >({
    success: "Template created successfully",
    failure: "Failed to create template",
    run: async (template) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.quoteLineItemTemplates.create({
          id,
          name: template.name,
          code: template.code ?? null,
          defaultAmount: template.default_amount ?? 1,
          defaultUnitPrice: template.default_unit_price ?? 0,
          lineItemType: template.line_item_type ?? "generic",
          category: template.category ?? null,
          sortOrder: template.sort_order ?? 0,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Template create rejected",
      );
      return toLegacyTemplate({
        id,
        name: template.name,
        code: template.code ?? null,
        defaultAmount: template.default_amount ?? 1,
        defaultUnitPrice: template.default_unit_price ?? 0,
        lineItemType: template.line_item_type ?? "generic",
        category: template.category ?? null,
        sortOrder: template.sort_order ?? 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
  });
}

export function useUpdateQuoteLineItemTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        name: TemplateTranslations;
        code: string | null;
        default_amount: number;
        default_unit_price: number;
        line_item_type: "generic" | "equipment" | "crew";
        category: string | null;
        sort_order: number;
      }>;
    },
    { id: string }
  >({
    success: "Template updated successfully",
    failure: "Failed to update template",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.quoteLineItemTemplates.update({
          id,
          name: updates.name,
          code: updates.code,
          defaultAmount: updates.default_amount,
          defaultUnitPrice: updates.default_unit_price,
          lineItemType: updates.line_item_type,
          category: updates.category,
          sortOrder: updates.sort_order,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Template update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteQuoteLineItemTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    success: "Template deleted successfully",
    failure: "Failed to delete template",
    run: async (id) => {
      const mutation = zero.mutate(
        appMutators.quoteLineItemTemplates.delete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Template delete rejected",
      );
      return id;
    },
  });
}

export function useRenameLineItemTemplateCategory() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { oldCategory: string; newCategory: string },
    { oldCategory: string; newCategory: string }
  >({
    success: "Category renamed successfully",
    failure: "Failed to rename category",
    run: async (input) => {
      const mutation = zero.mutate(
        appMutators.quoteLineItemTemplates.renameCategory(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Template category rename rejected",
      );
      return input;
    },
  });
}

export function resolveTemplateName(
  name: TemplateTranslations,
  language: string,
): string {
  return name[language] || name.de || Object.values(name)[0] || "";
}
